'use client';

import { useTranslations } from 'next-intl';
import { Pencil, Trash2 } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { getImageUrl } from '@/lib/image';
import { PermissionGate } from '@/features/auth';
import { formatLastLogin } from '../mappers/clients.mapper';
import type { Client } from '../types';

interface ClientCardProps {
  client: Client;
  onEdit: (client: Client) => void;
  onDelete: (client: Client) => void;
}

const STATUS_STYLES: Record<string, string> = {
  active: 'border-sky-500/30 bg-sky-500/10 text-sky-600 dark:text-sky-400',
  inactive: 'border-gray-500/30 bg-gray-500/10 text-gray-600 dark:text-gray-400'
};

export function ClientCard({ client, onEdit, onDelete }: ClientCardProps) {
  const t = useTranslations('clients');

  return (
    <Card className='border-border hover:border-primary/30 transition-colors'>
      <CardContent className='p-6'>
        <div className='mb-4 flex items-start justify-between'>
          <div className='flex items-center gap-3'>
            <Avatar className='h-10 w-10 rounded-md'>
              {client.logo_url && (
                <AvatarImage
                  src={getImageUrl(client.logo_url)}
                  alt={client.name}
                  className='object-cover'
                />
              )}
              <AvatarFallback className='rounded-md text-sm font-bold'>
                {client.name.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div>
              <h3 className='font-semibold'>{client.name}</h3>
              <p className='text-muted-foreground text-xs'>
                {client.client_id}
              </p>
            </div>
          </div>
          <Badge
            variant='outline'
            className={cn(
              'shrink-0 text-xs uppercase',
              STATUS_STYLES[client.status]
            )}
          >
            {client.status === 'active'
              ? t('statusActive')
              : t('statusInactive')}
          </Badge>
        </div>

        <div className='text-muted-foreground mb-4 space-y-1 text-sm'>
          <p>
            <a
              href={`mailto:${client.admin_email}`}
              className='hover:underline'
            >
              {client.admin_email}
            </a>
          </p>
          <p>
            {t('lastLogin')}:{' '}
            {formatLastLogin(client.last_login_at) ?? t('never')}
          </p>
        </div>

        <div className='flex items-center justify-end gap-1 border-t pt-3'>
          <PermissionGate permissions={['clients.edit']}>
            <Button
              variant='ghost'
              size='icon'
              className='h-8 w-8'
              onClick={() => onEdit(client)}
            >
              <Pencil className='h-4 w-4' />
            </Button>
          </PermissionGate>
          <PermissionGate permissions={['clients.delete']}>
            <Button
              variant='ghost'
              size='icon'
              className='text-destructive h-8 w-8'
              onClick={() => onDelete(client)}
            >
              <Trash2 className='h-4 w-4' />
            </Button>
          </PermissionGate>
        </div>
      </CardContent>
    </Card>
  );
}
